import { Controller } from "@hotwired/stimulus";
import {
  spreadsheetMangledPhones,
  SPREADSHEET_CORRUPTION_MESSAGE,
} from "../csv_quality";

// The guided campaign wizard. The load-bearing invariant is the merge-field
// cascade: the message's {{fields}} define the required recipient columns,
// which drive the CSV schema and per-group compatibility, so an audience that
// cannot satisfy the message is never selectable.
//
// Field detection mirrors TemplateRenderer::PLACEHOLDER server-side.
// Connects to data-controller="campaign-wizard".
export default class extends Controller {
  static targets = [
    "stepPanel",
    "stepCircle",
    "stepLabel",
    "stepBar",
    "nameInput",
    "msgModeBtn",
    "savedPanel",
    "newPanel",
    "templateCard",
    "newName",
    "newBody",
    "detectedWrap",
    "detectedChips",
    "saveTpl",
    "audModeBtn",
    "groupPanel",
    "csvPanel",
    "pastePanel",
    "columnsText",
    "columnsChips",
    "groupCard",
    "csvHint",
    "schemaHead",
    "schemaRow",
    "downloadLink",
    "csvMeta",
    "csvError",
    "makeGroup",
    "groupNameWrap",
    "groupNameInput",
    "pasteInput",
    "pasteWarning",
    "pasteWarningFields",
    "previewCard",
    "previewBody",
    "previewMeta",
    "previewRestore",
    "sumTemplate",
    "sumFields",
    "sumRecipients",
    "sumCost",
    "balanceWarning",
    "reviewName",
    "reviewTemplate",
    "reviewRecipients",
    "reviewFields",
    "scheduleToggle",
    "scheduleFields",
    "scheduleDate",
    "scheduleTime",
    "backBtn",
    "continueBtn",
    "submitBtn",
    "submitLabel",
    "form",
    "fieldName",
    "fieldTemplateId",
    "fieldRecipients",
    "fieldGroupIds",
    "fieldScheduledAt",
    "fieldAfterSave",
  ];

  static outlets = ["dropzone"];

  static values = {
    compatibilityUrl: String,
    schemaCsvUrl: String,
    importAudienceUrl: String,
    templatesUrl: String,
    estimateUrl: String,
    cancelUrl: String,
    balance: Number,
    currency: String,
    mode: { type: String, default: "new" },
  };

  static PLACEHOLDER = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;

  connect() {
    this.step = 0;
    this.msgMode = "saved";
    this.audMode = "group";
    this.csvFile = null;
    this.csvRows = 0;
    this.csvSample = null;
    this.costPerMessage = null;
    this.groupChecks = [];
    if (
      this.modeValue === "edit" &&
      this.pasteInputTarget.value.trim() !== ""
    ) {
      this.audMode = "paste";
    }
    this.render();
  }

  // ---- step navigation ----

  next() {
    if (!this.stepValid()) return;
    if (this.step === 1) this.refreshCompatibility();
    if (this.step === 2) this.refreshEstimate();
    this.step = Math.min(3, this.step + 1);
    this.render();
  }

  back() {
    if (this.step === 0) {
      window.location.assign(this.cancelUrlValue);
      return;
    }
    this.step -= 1;
    this.render();
  }

  jump(event) {
    const target = Number(event.currentTarget.dataset.step);
    if (target >= this.step) return;
    this.step = target;
    this.render();
  }

  // ---- step 2: message ----

  setMsgMode(event) {
    this.msgMode = event.currentTarget.dataset.mode;
    this.render();
  }

  pickTemplate(event) {
    this.templateCardTargets.forEach((card) => {
      card.dataset.selected = (card === event.currentTarget).toString();
    });
    this.render();
  }

  bodyTyped() {
    this.render();
  }

  // ---- step 3: audience ----

  setAudMode(event) {
    this.audMode = event.currentTarget.dataset.mode;
    this.render();
  }

  switchToCsv(event) {
    event.preventDefault();
    this.audMode = "csv";
    this.render();
  }

  pickGroup(event) {
    const card = event.currentTarget;
    if (card.dataset.compatible !== "true") return;
    this.groupCardTargets.forEach((g) => {
      g.dataset.selected = (g === card).toString();
    });
    this.render();
  }

  // The dropzone (shared controller) owns the file-choosing UX; these two
  // react to its events.
  csvChosen({ detail: { file } }) {
    this.acceptCsv(file);
  }

  csvCleared() {
    this.csvFile = null;
    this.csvRows = 0;
    this.csvSample = null;
    this.render();
  }

  toggleMakeGroup() {
    this.render();
  }

  pasteTyped() {
    this.render();
  }

  // ---- step 4: review ----

  dismissPreview() {
    this.previewCardTarget.classList.add("hidden");
    this.previewRestoreTarget.classList.remove("hidden");
  }

  restorePreview() {
    this.previewCardTarget.classList.remove("hidden");
    this.previewRestoreTarget.classList.add("hidden");
  }

  toggleSchedule() {
    this.render();
  }

  async submit(event) {
    event.preventDefault();
    if (!this.stepValid()) return;
    this.submitBtnTarget.disabled = true;

    try {
      const templateId = await this.ensureTemplate();
      const phones = await this.resolveAudience();

      this.fieldNameTarget.value = this.campaignName();
      this.fieldTemplateIdTarget.value = templateId;
      this.fieldRecipientsTarget.value = phones.join("\n");
      const groupMode = this.audMode === "group";
      this.fieldGroupIdsTarget.value = groupMode ? this.selectedGroupId() : "";
      this.fieldGroupIdsTarget.disabled = !groupMode;
      if (this.modeValue === "edit") {
        this.fieldAfterSaveTarget.value = "";
      } else if (this.scheduling()) {
        this.fieldScheduledAtTarget.value = `${this.scheduleDateTarget.value}T${this.scheduleTimeTarget.value}`;
        this.fieldAfterSaveTarget.value = "schedule";
      } else {
        this.fieldScheduledAtTarget.value = "";
        this.fieldAfterSaveTarget.value = "send_now";
      }
      this.formTarget.submit();
    } catch (error) {
      this.submitBtnTarget.disabled = false;
      window.showToast(
        error.message || "Could not create the campaign",
        "error",
      );
    }
  }

  // The model requires a template: saved mode uses the picked one; new mode
  // always persists one (unchecking "save as reusable" just names it one-off).
  async ensureTemplate() {
    if (this.msgMode === "saved")
      return this.selectedTemplateCard().dataset.templateId;

    const reusable = this.saveTplTarget.checked;
    const name = (
      this.newNameTarget.value.trim() ||
      `${this.campaignName()}${reusable ? "" : " (one-off)"}`
    ).slice(0, 80);
    const response = await fetch(this.templatesUrlValue, {
      method: "POST",
      headers: this.jsonHeaders(),
      body: JSON.stringify({
        message_template: { name: name, body: this.body() },
      }),
    });
    const data = await response.json();
    if (!response.ok)
      throw new Error(
        (data.errors || []).join(", ") || "Template could not be saved",
      );
    return data.id;
  }

  // Group mode submits group_ids (server resolves member phones); CSV mode
  // imports now (upsert + optional group) and submits the returned phones;
  // paste mode submits the pasted numbers as-is.
  async resolveAudience() {
    if (this.audMode === "group") return [];
    if (this.audMode === "paste") return this.pastedNumbers();

    const form = new FormData();
    form.append("csv_file", this.csvFile);
    if (this.makeGroupTarget.checked)
      form.append("group_name", this.groupNameInputTarget.value.trim());
    const response = await fetch(this.importAudienceUrlValue, {
      method: "POST",
      headers: { "X-CSRF-Token": this.csrfToken(), Accept: "application/json" },
      body: form,
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || "CSV import failed");
    if (!data.phones.length)
      throw new Error("The CSV contained no valid phone numbers");
    return data.phones;
  }

  // ---- cascade primitives ----

  body() {
    if (this.msgMode === "saved") {
      const card = this.selectedTemplateCard();
      return card ? card.dataset.body : "";
    }
    return this.newBodyTarget.value;
  }

  fields() {
    return [
      ...new Set(
        [...this.body().matchAll(this.constructor.PLACEHOLDER)].map((m) =>
          m[1].toLowerCase(),
        ),
      ),
    ];
  }

  columns() {
    return ["phone", ...this.fields()];
  }

  campaignName() {
    return this.nameInputTarget.value.trim();
  }

  selectedTemplateCard() {
    return this.templateCardTargets.find(
      (card) => card.dataset.selected === "true",
    );
  }

  selectedGroupId() {
    const card = this.groupCardTargets.find(
      (g) => g.dataset.selected === "true",
    );
    return card ? card.dataset.groupId : "";
  }

  pastedNumbers() {
    return this.pasteInputTarget.value
      .split(/\n/)
      .map((s) => s.trim())
      .filter(Boolean);
  }

  recipientsCount() {
    if (this.audMode === "group") {
      const card = this.groupCardTargets.find(
        (g) => g.dataset.selected === "true",
      );
      return card ? Number(card.dataset.members) : 0;
    }
    if (this.audMode === "csv") return this.csvRows;
    return this.pastedNumbers().length;
  }

  scheduling() {
    return this.hasScheduleToggleTarget && this.scheduleToggleTarget.checked;
  }

  stepValid() {
    switch (this.step) {
      case 0:
        return this.campaignName().length > 0;
      case 1:
        return this.msgMode === "saved"
          ? !!this.selectedTemplateCard()
          : this.body().trim().length > 0;
      case 2:
        if (this.recipientsCount() === 0) return false;
        if (
          this.audMode === "csv" &&
          this.makeGroupTarget.checked &&
          this.groupNameInputTarget.value.trim() === ""
        )
          return false;
        return true;
      default:
        return this.scheduling()
          ? this.scheduleDateTarget.value !== "" &&
              this.scheduleTimeTarget.value !== ""
          : true;
    }
  }

  // ---- server round-trips at the natural seams ----

  async refreshCompatibility() {
    try {
      const response = await fetch(this.compatibilityUrlValue, {
        method: "POST",
        headers: this.jsonHeaders(),
        body: JSON.stringify({ body: this.body() }),
      });
      const data = await response.json();
      this.groupChecks = data.groups || [];
      this.applyCompatibility();
    } catch {
      this.groupChecks = [];
    }
  }

  applyCompatibility() {
    const checks = new Map(this.groupChecks.map((g) => [String(g.id), g]));
    this.groupCardTargets.forEach((card) => {
      const check = checks.get(card.dataset.groupId);
      const compatible = check ? check.compatible : false;
      card.dataset.compatible = compatible.toString();
      if (!compatible) card.dataset.selected = "false";

      const badge = card.querySelector("[data-compat-badge]");
      const missing = check
        ? Object.keys(check.missing)
            .map((v) => `{{${v}}}`)
            .join(", ")
        : "";
      if (compatible) {
        badge.className =
          "inline-flex items-center gap-1 text-xs font-semibold text-primary-700 dark:text-primary-400";
        badge.textContent = "Compatible";
      } else {
        badge.className =
          "inline-flex items-center gap-1 text-xs font-semibold text-neutral-400 dark:text-neutral-500";
        badge.textContent =
          check && check.members > 0 ? `Missing ${missing}` : "No members";
      }
    });
    this.render();
  }

  async refreshEstimate() {
    try {
      const response = await fetch(this.estimateUrlValue, {
        method: "POST",
        headers: this.jsonHeaders(),
        body: JSON.stringify({ message: this.body() }),
      });
      const data = await response.json();
      this.costPerMessage = Number(data.total_cost) || 0;
    } catch {
      this.costPerMessage = null;
    }
    this.render();
  }

  acceptCsv(file) {
    const reader = new FileReader();
    reader.onload = () => {
      const lines = reader.result
        .split(/\r?\n/)
        .filter((line) => line.trim().length > 0);
      const header = this.splitCsvLine(lines[0] || "").map((c) =>
        c.toLowerCase(),
      );
      const missing = this.columns().filter((c) => !header.includes(c));
      const rows = lines.slice(1).map((line) => this.splitCsvLine(line));

      if (lines.length < 2) {
        this.rejectCsv("The file has a header but no recipient rows.");
      } else if (spreadsheetMangledPhones(header, rows)) {
        this.rejectCsv(SPREADSHEET_CORRUPTION_MESSAGE);
      } else if (missing.length > 0) {
        this.rejectCsv(
          `Missing columns: ${missing.join(", ")}. Download the schema below and fill it.`,
        );
      } else {
        this.csvFile = file;
        this.csvRows = lines.length - 1;
        const cells = this.splitCsvLine(lines[1]);
        this.csvSample = Object.fromEntries(
          header.map((h, i) => [h, cells[i]]),
        );
      }
      this.render();
    };
    reader.readAsText(file.slice(0, 256 * 1024));
  }

  // Quote-aware CSV cell split (preview only — the import parses server-side)
  splitCsvLine(line) {
    const cells = [...line.matchAll(/(?:^|,)\s*(?:"([^"]*)"|([^",]*))/g)];
    return cells.map((m) => (m[1] != null ? m[1] : m[2] || "").trim());
  }

  // An invalid file never becomes state: surface the reason and reset the
  // dropzone chip (its cleared event zeroes csvFile/csvRows/csvSample).
  rejectCsv(message) {
    if (this.hasDropzoneOutlet) this.dropzoneOutlet.clear();
    this.csvFile = null;
    this.csvRows = 0;
    this.csvSample = null;
    this.csvErrorTarget.textContent = message;
    this.csvErrorTarget.classList.remove("hidden");
  }

  // ---- rendering ----

  render() {
    const fields = this.fields();
    const personalised = fields.length > 0;
    const recipients = this.recipientsCount();

    // stepper
    this.stepPanelTargets.forEach((panel, i) =>
      panel.classList.toggle("hidden", i !== this.step),
    );
    this.stepCircleTargets.forEach((circle, i) => {
      const done = i < this.step;
      const active = i === this.step;
      circle.className =
        "flex h-8 w-8 flex-none items-center justify-center rounded-full text-sm font-bold " +
        (done
          ? "bg-primary-600 text-white"
          : active
            ? "bg-secondary-900 text-white dark:bg-secondary-700"
            : "bg-neutral-100 text-neutral-500 border border-neutral-300 dark:bg-neutral-800 dark:text-neutral-400 dark:border-neutral-700");
      circle.textContent = done ? "" : String(i + 1);
      circle.querySelector("svg")?.remove();
      if (done) circle.appendChild(this.checkIcon());
    });
    this.stepLabelTargets.forEach((label, i) => {
      label.className =
        "text-sm " +
        (i === this.step
          ? "font-semibold text-neutral-900 dark:text-white"
          : i < this.step
            ? "font-medium text-neutral-700 dark:text-neutral-300"
            : "font-medium text-neutral-400 dark:text-neutral-500");
    });
    this.stepBarTargets.forEach((bar, i) => {
      bar.className =
        "mx-3 h-0.5 flex-1 rounded " +
        (i < this.step
          ? "bg-primary-400"
          : "bg-neutral-200 dark:bg-neutral-700");
    });

    // step 2 panels
    this.msgModeBtnTargets.forEach((btn) =>
      this.styleSegment(btn, btn.dataset.mode === this.msgMode),
    );
    if (this.hasSavedPanelTarget)
      this.savedPanelTarget.classList.toggle(
        "hidden",
        this.msgMode !== "saved",
      );
    if (this.hasNewPanelTarget)
      this.newPanelTarget.classList.toggle("hidden", this.msgMode !== "new");
    this.templateCardTargets.forEach((card) => {
      const on = card.dataset.selected === "true";
      card.classList.toggle("border-primary-400", on);
      card.classList.toggle("bg-primary-50", on);
      card.classList.toggle("dark:bg-primary-900/20", on);
      card.querySelector("[data-radio-dot]").classList.toggle("hidden", !on);
    });
    if (this.msgMode === "new") {
      this.detectedWrapTarget.classList.toggle("hidden", !personalised);
      this.detectedChipsTarget.innerHTML = "";
      fields.forEach((f) =>
        this.detectedChipsTarget.appendChild(this.chip(f, "green")),
      );
    }

    // step 3: columns banner + panels
    this.columnsTextTarget.innerHTML = personalised
      ? "This message is <strong>personalised</strong>, so every recipient needs these columns:"
      : "This message has <strong>no merge fields</strong> — recipients only need a phone number.";
    this.columnsChipsTarget.innerHTML = "";
    if (personalised) {
      this.columns().forEach((c, i) =>
        this.columnsChipsTarget.appendChild(
          this.chip(c, i === 0 ? "muted" : "green"),
        ),
      );
    }
    this.columnsChipsTarget.classList.toggle("hidden", !personalised);

    this.audModeBtnTargets.forEach((btn) =>
      this.styleSegment(btn, btn.dataset.mode === this.audMode),
    );
    this.groupPanelTarget.classList.toggle("hidden", this.audMode !== "group");
    this.csvPanelTarget.classList.toggle("hidden", this.audMode !== "csv");
    this.pastePanelTarget.classList.toggle("hidden", this.audMode !== "paste");
    this.csvHintTarget.classList.toggle("hidden", !personalised);

    this.groupCardTargets.forEach((card) => {
      const on = card.dataset.selected === "true";
      const ok = card.dataset.compatible === "true";
      card.classList.toggle("border-primary-400", on);
      card.classList.toggle("bg-primary-50", on);
      card.classList.toggle("dark:bg-primary-900/20", on);
      card.classList.toggle("opacity-60", !ok);
      card.classList.toggle("cursor-not-allowed", !ok);
      card.classList.toggle("cursor-pointer", ok);
    });

    // CSV schema preview + download link
    this.schemaHeadTarget.innerHTML = "";
    this.schemaRowTarget.innerHTML = "";
    this.columns().forEach((c) => {
      const th = document.createElement("div");
      th.className =
        "flex-1 px-3 py-2 font-semibold text-neutral-900 dark:text-neutral-100 border-r border-neutral-200 dark:border-neutral-700 last:border-r-0 truncate";
      th.textContent = c;
      this.schemaHeadTarget.appendChild(th);
      const td = document.createElement("div");
      td.className =
        "flex-1 px-3 py-2 text-neutral-400 dark:text-neutral-500 border-r border-neutral-200 dark:border-neutral-700 last:border-r-0 truncate";
      td.textContent = c === "phone" ? "+256700000000" : `${c}…`;
      this.schemaRowTarget.appendChild(td);
    });
    const params = new URLSearchParams({
      body: this.body(),
      name: this.campaignName() || "campaign",
    });
    this.downloadLinkTarget.href = `${this.schemaCsvUrlValue}?${params}`;

    // CSV upload state (the dropzone shows the chip; we add the match meta)
    const hasFile = !!this.csvFile;
    if (hasFile) {
      this.csvErrorTarget.classList.add("hidden");
      this.csvMetaTarget.textContent = `${this.csvRows.toLocaleString()} rows · all ${this.columns().length} columns matched`;
    }
    this.groupNameWrapTarget.classList.toggle(
      "hidden",
      !(hasFile && this.makeGroupTarget.checked),
    );

    // paste warning
    this.pasteWarningTarget.classList.toggle("hidden", !personalised);
    if (personalised) {
      this.pasteWarningFieldsTarget.textContent = fields
        .map((f) => `{{${f}}}`)
        .join(", ");
    }

    // review + summary rail
    const templateName =
      this.msgMode === "saved"
        ? this.selectedTemplateCard()?.dataset.templateName || "—"
        : this.newNameTarget.value.trim() || "New template";
    this.previewBodyTarget.textContent = this.previewText();
    this.previewMetaTarget.textContent = `${this.body().length} chars · ${this.parts()} part${this.parts() === 1 ? "" : "s"}`;
    this.reviewNameTarget.textContent = this.campaignName() || "—";
    this.reviewTemplateTarget.textContent = templateName;
    this.reviewRecipientsTarget.textContent = recipients.toLocaleString();
    this.reviewFieldsTarget.textContent = personalised
      ? fields.join(", ")
      : "none";
    this.sumTemplateTarget.textContent = this.step >= 1 ? templateName : "—";
    this.sumFieldsTarget.textContent = personalised
      ? String(fields.length)
      : "none";
    this.sumRecipientsTarget.textContent = recipients.toLocaleString();

    const cost =
      this.costPerMessage == null ? null : recipients * this.costPerMessage;
    this.sumCostTarget.textContent =
      cost == null ? "—" : `~${this.currencyValue} ${cost.toLocaleString()}`;
    this.balanceWarningTarget.classList.toggle(
      "hidden",
      !(cost != null && cost > this.balanceValue && recipients > 0),
    );

    if (this.hasScheduleFieldsTarget) {
      this.scheduleFieldsTarget.classList.toggle("hidden", !this.scheduling());
    }
    this.submitLabelTarget.textContent =
      this.modeValue === "edit"
        ? "Save changes"
        : this.scheduling()
          ? "Schedule campaign"
          : `Send to ${recipients.toLocaleString()} now`;

    // nav buttons
    this.backBtnTarget.textContent = this.step === 0 ? "Cancel" : "Back";
    this.continueBtnTarget.classList.toggle("hidden", this.step === 3);
    this.submitBtnTarget.classList.toggle("hidden", this.step !== 3);
    this.continueBtnTarget.disabled = !this.stepValid();
    this.submitBtnTarget.disabled = !this.stepValid();
  }

  previewText() {
    const sample = this.csvSample || {};
    return this.body().replace(this.constructor.PLACEHOLDER, (_, key) => {
      const value = sample[key.toLowerCase()];
      return value != null && value !== "" ? value : `[${key.toLowerCase()}]`;
    });
  }

  parts() {
    return Math.max(1, Math.ceil((this.body().length || 1) / 160));
  }

  styleSegment(btn, on) {
    btn.className =
      "inline-flex items-center gap-2 rounded-lg px-3.5 py-2 text-sm font-semibold transition-colors " +
      (on
        ? "bg-white text-neutral-900 shadow-xs dark:bg-neutral-700 dark:text-white"
        : "text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200");
  }

  chip(name, tone) {
    const tones = {
      green:
        "bg-primary-50 text-primary-700 border-primary-200 dark:bg-primary-900/20 dark:text-primary-400 dark:border-primary-800",
      navy: "bg-secondary-50 text-secondary-700 border-secondary-200 dark:bg-secondary-800 dark:text-secondary-300 dark:border-secondary-700",
      muted:
        "bg-neutral-100 text-neutral-500 border-neutral-200 dark:bg-neutral-800 dark:text-neutral-400 dark:border-neutral-700",
    };
    const chip = document.createElement("span");
    chip.className = `inline-flex items-center gap-1 rounded-md border px-2 py-0.5 font-mono text-xs font-medium ${tones[tone]}`;
    chip.textContent = name;
    return chip;
  }

  checkIcon() {
    // Tiny inline check, built via DOM (the heroicon helper is server-side only)
    const ns = "http://www.w3.org/2000/svg";
    const svg = document.createElementNS(ns, "svg");
    svg.setAttribute("viewBox", "0 0 20 20");
    svg.setAttribute("fill", "currentColor");
    svg.classList.add("h-4", "w-4");
    const path = document.createElementNS(ns, "path");
    path.setAttribute("fill-rule", "evenodd");
    path.setAttribute(
      "d",
      "M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z",
    );
    path.setAttribute("clip-rule", "evenodd");
    svg.appendChild(path);
    return svg;
  }

  jsonHeaders() {
    return {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-CSRF-Token": this.csrfToken(),
    };
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || "";
  }
}
